import { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

const PERMS = ['view', 'create', 'edit', 'delete', 'export'];

interface ModuleEntry {
  name: string;
  type: 'M' | 'S';
  parentId: string | null;
  parentName: string | null;
  orderNumber: number;
  isSystem: boolean;
}

function buildEmptyPermissions(modules: ModuleEntry[]): Record<string, Record<string, boolean>> {
  const p: Record<string, Record<string, boolean>> = {};
  for (const mod of modules) {
    p[mod.name] = {};
    for (const perm of PERMS) p[mod.name][perm] = false;
  }
  return p;
}

function mergePermissions(
  saved: any,
  modules: ModuleEntry[],
): Record<string, Record<string, boolean>> {
  const base = buildEmptyPermissions(modules);
  if (!saved || typeof saved !== 'object') return base;
  for (const mod of modules) {
    if (saved[mod.name]) {
      for (const perm of PERMS) {
        base[mod.name][perm] = !!saved[mod.name][perm];
      }
    }
  }
  // Also preserve any module the role already has saved permissions for
  // even if it is not in the current modules list (e.g. super-admin
  // deleted/renamed it after the role was saved) — keeps history alive.
  for (const key of Object.keys(saved)) {
    if (!base[key] && saved[key] && typeof saved[key] === 'object') {
      base[key] = {};
      for (const perm of PERMS) base[key][perm] = !!saved[key][perm];
    }
  }
  return base;
}

interface Props {
  role?: any;
  onClose: () => void;
  onSaved: () => void;
}

export function RoleFormDrawer({ role, onClose, onSaved }: Props) {
  const isEdit = !!role;
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [modules, setModules] = useState<ModuleEntry[]>([]);
  const [modulesLoading, setModulesLoading] = useState(true);

  const [form, setForm] = useState({
    name: role?.name || '',
    description: role?.description || '',
    isActive: role?.isActive ?? true,
    permissions: {} as Record<string, Record<string, boolean>>,
  });

  // Modules drive the permission grid. System modules (hardcoded in
  // backend) are always present; custom ones added by super-admin via
  // the AppModule catalog show up here automatically.
  useEffect(() => {
    let cancelled = false;
    api.get('/roles/modules')
      .then(({ data }) => {
        if (cancelled) return;
        const list: ModuleEntry[] = Array.isArray(data) ? data : [];
        setModules(list);
        setForm(f => ({ ...f, permissions: mergePermissions(role?.permissions, list) }));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setModulesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  const togglePerm = (mod: string, perm: string) => {
    setForm(f => ({
      ...f,
      permissions: {
        ...f.permissions,
        [mod]: { ...(f.permissions[mod] || {}), [perm]: !f.permissions[mod]?.[perm] },
      },
    }));
  };

  const toggleAllModule = (mod: string) => {
    const allOn = PERMS.every(p => form.permissions[mod]?.[p]);
    setForm(f => ({
      ...f,
      permissions: {
        ...f.permissions,
        [mod]: Object.fromEntries(PERMS.map(p => [p, !allOn])),
      },
    }));
  };

  const toggleAllPerm = (perm: string) => {
    const allOn = modules.every(m => form.permissions[m.name]?.[perm]);
    setForm(f => {
      const newPerms = { ...f.permissions };
      for (const mod of modules) {
        newPerms[mod.name] = { ...(newPerms[mod.name] || {}), [perm]: !allOn };
      }
      return { ...f, permissions: newPerms };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/roles/${role.id}`, form);
      } else {
        await api.post('/roles', form);
      }
      onSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save role');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="w-full sm:w-[620px] sm:max-w-none flex-shrink-0 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Role' : 'New Role'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Basic Info */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Basic Information</h3>

            <div className="space-y-1.5">
              <Label>Role Name <span className="text-red-500">*</span></Label>
              <Input
                placeholder="e.g. Sales Manager"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                required
                disabled={isEdit && role?.isDefault}
              />
              {isEdit && role?.isDefault && (
                <p className="text-xs text-muted-foreground">Default role names cannot be changed</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                placeholder="What can this role do?"
                value={form.description}
                onChange={e => setField('description', e.target.value)}
              />
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
              <div>
                <p className="text-sm font-medium">Role Status</p>
                <p className="text-xs text-muted-foreground">Inactive roles cannot be assigned to users</p>
              </div>
              <button
                type="button"
                onClick={() => setField('isActive', !form.isActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-blue-600' : 'bg-slate-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </section>

          {/* Permissions Matrix */}
          <section className="space-y-3">
            <div className="flex items-baseline justify-between">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Permissions</h3>
              <p className="text-[11px] text-slate-400">
                New modules? Super-admin adds them in the Modules catalog.
              </p>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr className="border-b-2 border-slate-200">
                    <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider w-36">Module</th>
                    {PERMS.map(perm => (
                      <th key={perm} className="px-2 py-2.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">
                        <div className="flex flex-col items-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleAllPerm(perm)}
                            className="text-xs capitalize hover:text-blue-600 transition-colors font-medium"
                            title={`Toggle all ${perm}`}
                          >
                            {perm.charAt(0).toUpperCase() + perm.slice(1)}
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modulesLoading ? (
                    <tr>
                      <td colSpan={PERMS.length + 1} className="px-4 py-6 text-center text-xs text-slate-400">
                        <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                        Loading modules…
                      </td>
                    </tr>
                  ) : modules.length === 0 ? (
                    <tr>
                      <td colSpan={PERMS.length + 1} className="px-4 py-6 text-center text-xs text-slate-400">
                        No modules configured.
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      // Group sub-pages under their parent main module so
                      // the grid reads as a tree. Anything that has no
                      // parent or whose parent is filtered out falls to
                      // the end as an orphan row.
                      const mains = modules
                        .filter(m => m.type === 'M')
                        .sort((a, b) => a.orderNumber - b.orderNumber);
                      const childrenByParent = new Map<string, ModuleEntry[]>();
                      for (const s of modules.filter(m => m.type === 'S' && m.parentName)) {
                        const key = s.parentName!;
                        const arr = childrenByParent.get(key) ?? [];
                        arr.push(s);
                        childrenByParent.set(key, arr);
                      }
                      for (const arr of childrenByParent.values()) {
                        arr.sort((a, b) => a.orderNumber - b.orderNumber);
                      }
                      const rendered: React.ReactNode[] = [];
                      let rowIdx = 0;

                      const renderRow = (mod: ModuleEntry, isChild: boolean) => {
                        const row = form.permissions[mod.name] ?? {};
                        const allOn = PERMS.every(p => row[p]);
                        const r = rowIdx++;
                        return (
                          <tr key={mod.name} className={r % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => toggleAllModule(mod.name)}
                                className={`flex items-center gap-2 text-left hover:text-blue-600 transition-colors ${isChild ? 'pl-5' : ''}`}
                                title={`Toggle all for ${mod.name}`}
                              >
                                {isChild ? (
                                  <span className="text-slate-300 text-xs flex-shrink-0">↳</span>
                                ) : (
                                  <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${allOn ? 'bg-blue-500' : 'bg-slate-300'}`} />
                                )}
                                <span className={`text-sm ${isChild ? 'text-slate-600' : 'font-medium text-slate-700'}`}>
                                  {mod.name}
                                </span>
                              </button>
                            </td>
                            {PERMS.map(perm => (
                              <td key={perm} className="px-2 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!row[perm]}
                                  onChange={() => togglePerm(mod.name, perm)}
                                  className="h-4 w-4 rounded border-slate-300 text-blue-600 cursor-pointer accent-blue-600"
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      };

                      const consumedChildren = new Set<string>();
                      for (const main of mains) {
                        rendered.push(renderRow(main, false));
                        const subs = childrenByParent.get(main.name) ?? [];
                        for (const s of subs) {
                          rendered.push(renderRow(s, true));
                          consumedChildren.add(s.name);
                        }
                      }
                      // Orphans — sub-pages whose parent is not in this filtered view.
                      for (const arr of childrenByParent.values()) {
                        for (const s of arr) {
                          if (consumedChildren.has(s.name)) continue;
                          rendered.push(renderRow(s, false));
                        }
                      }
                      return rendered;
                    })()
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">Click a column header to toggle all, or a module name to toggle the row.</p>
          </section>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 border border-red-200">{error}</div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0 bg-white">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit as any} disabled={isSaving || modulesLoading}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Role'}
          </Button>
        </div>
      </div>
    </div>
  );
}
